// The one question when a zone is edited (settings step 2, docs/reference/
// zone_and_settings_changes.md §9): what happened at this spot? The answer decides what
// the zone's history does — the backend's supersede `change_kind`.
//
// Pure (unit-tested): the texts, what changed, and the one suggestion we make.

import { i18n } from '@/i18n'
import { reidMergeDefaultSec, maxPlausibleDefaultSec, splitMergeDefaultSec } from '@/utils/zoneMatchingDefaults'

// Texts are getters: read in the portal's language at the moment they are shown.
const t = (key, params) => i18n.global.t(`zoneSettings.change.${key}`, params)
const option = (kind, labelKey, hintKey) => ({
  kind,
  get label() { return t(labelKey) },
  get hint() { return t(hintKey) },
})

export const CHANGE_OPTIONS = [
  option('corrected', 'corrected', 'correctedHint'),
  option('changed', 'changed', 'changedHint'),
  option('different', 'different', 'differentHint'),
]

// "The camera was moved" (settings step 2e): asked for every zone on the camera, also
// the ones that were not redrawn. "I corrected a mistake" reads oddly there.
export const CAMERA_MOVED_OPTIONS = [
  option('corrected', 'sameSpot', 'sameSpotHint'),
  ...CHANGE_OPTIONS.slice(1),
]
// Stored as the marker label on the zone (data), so it stays English.
export const CAMERA_MOVED_LABEL = 'Camera was moved'

// A visit-matching setting changed (§9 item 4, step 2b): from today on, or also the past.
export const MATCHING_OPTIONS = [
  option('from_today', 'fromToday', 'fromTodayHint'),
  option('correct_past', 'correctPast', 'correctPastHint'),
]
export const DIRECTION_LABEL = 'Counting direction'
// DIRECTION_LABEL in the portal's language, for display.
export const directionLabel = () => t('direction')

const parse = (v) => {
  if (v === null || v === undefined) return null
  if (typeof v !== 'string') return v
  try { return JSON.parse(v) } catch { return null }
}
const pts = (v) => {
  const p = parse(v)
  return Array.isArray(p) && p.length >= 3 ? p.map(([x, y]) => [Number(x), Number(y)]) : null
}

// A name can change in a way nobody can see. 'Shelf ' → 'Shelf' is a real rename —
// the history of a zone turns on the answer — and it printed as two identical
// strings: cid 3's 'Gang Veiligheidsschoenen 1 ' asked the manager to decide about a
// change the dialog could not show them. Both names are quoted, which puts a stray
// space in plain view, and when that spacing is the ONLY difference it is said in
// words as well, since a reader should not have to count characters between quotes.
function nameChange(before, after) {
  const same = String(before).replace(/\s+/g, ' ').trim() === String(after).replace(/\s+/g, ' ').trim()
  return t('name', { before, after }) + (same ? ` ${t('spacingOnly')}` : '')
}

// What changed, in words — one line per kind of change.
export function whatChanged(oldZone, newZone, typeName = (id) => String(id)) {
  const out = []
  if (oldZone.DetectionZoneName !== newZone.DetectionZoneName) {
    out.push(nameChange(oldZone.DetectionZoneName, newZone.DetectionZoneName))
  }
  if (Number(oldZone.DetectionZoneTypeId) !== Number(newZone.DetectionZoneTypeId)) {
    out.push(t('type', { from: typeName(oldZone.DetectionZoneTypeId), to: typeName(newZone.DetectionZoneTypeId) }))
  }
  if (JSON.stringify(pts(oldZone.DetectionZonePointArray)) !== JSON.stringify(pts(newZone.DetectionZonePointArray))) {
    out.push(t('shape'))
  }
  const tw = (z) => JSON.stringify((parse(z.DetectionZoneTripwirePoints) || []).map((t) => (t && t.points) || t))
  if (tw(oldZone) !== tw(newZone)) out.push(t('line'))
  return out
}

// ---- do two polygons overlap at all? (edges cross, or one lies inside the other) ----
function inside([x, y], poly) {
  let hit = false
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i]
    const [xj, yj] = poly[j]
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) hit = !hit
  }
  return hit
}
function cross(a, b, c, d) {
  const o = (p, q, r) => Math.sign((q[0] - p[0]) * (r[1] - p[1]) - (q[1] - p[1]) * (r[0] - p[0]))
  return o(a, b, c) !== o(a, b, d) && o(c, d, a) !== o(c, d, b)
}
export function polygonsOverlap(a, b) {
  if (!a || !b) return true                       // unknown: never claim "no overlap"
  if (a.some((p) => inside(p, b)) || b.some((p) => inside(p, a))) return true
  for (let i = 0; i < a.length; i++) {
    for (let j = 0; j < b.length; j++) {
      if (cross(a[i], a[(i + 1) % a.length], b[j], b[(j + 1) % b.length])) return true
    }
  }
  return false
}

// The one pre-selection: "a different spot" when the new area does not overlap the old
// one at all (a natural boundary, not a picked threshold). Otherwise nothing is chosen.
export function suggestedKind(oldZone, newZone) {
  const a = pts(oldZone.DetectionZonePointArray)
  const b = pts(newZone.DetectionZonePointArray)
  return a && b && !polygonsOverlap(a, b) ? 'different' : null
}

// Did a counting direction change? The zone-level picker (DirectionsCombinedEE) or one
// per counting line (TripwireDirections[id]); only the direction itself, not the anchor.
const DIRECTION_FIELDS = ['vertical', 'horizontal', 'movementPattern']
const dirOf = (d) => (d && typeof d === 'object' ? DIRECTION_FIELDS.map((k) => d[k] ?? null) : null)
export function directionChanged(oldSettings, newSettings) {
  const a = parse(oldSettings) || {}
  const b = parse(newSettings) || {}
  if (JSON.stringify(dirOf(a.DirectionsCombinedEE)) !== JSON.stringify(dirOf(b.DirectionsCombinedEE))) return true
  const ta = a.TripwireDirections || {}
  const tb = b.TripwireDirections || {}
  // A line that is new or gone is a change of the line, asked on the flow's Save.
  return Object.keys(tb).some((id) => id in ta && JSON.stringify(dirOf(ta[id])) !== JSON.stringify(dirOf(tb[id])))
}

// Which visit-matching settings changed, by the value that APPLIES (the zone type's
// default filled in: "10 s" and "default" are the same setting) — in words, as the
// settings window names them. Mirrors backend tools/matching_versions.changed_keys.
const MATCHING = [
  ['SplitVisitMergeThreshold', 'splitMerge', () => splitMergeDefaultSec()],
  ['ReIDMergeThresholdSec', 'reidMerge', (t) => reidMergeDefaultSec(t)],
  ['MaxPlausibleVisitDurationSec', 'maxPlausible', (t) => maxPlausibleDefaultSec(t)],
  ['SameIdInOutUniqueData', 'sameId', () => false],
  ['SameIdWindowHours', 'sameId', () => 1],
]
export function matchingChanged(oldSettings, newSettings, typeName) {
  const a = parse(oldSettings) || {}
  const b = parse(newSettings) || {}
  const applied = (s, key, dflt) => {
    const v = s[key]
    if (v === undefined || v === null || v === '') return dflt(typeName)
    return typeof v === 'boolean' ? v : Number(v)
  }
  const out = []
  for (const [key, wordsKey, dflt] of MATCHING) {
    const words = t(wordsKey)
    const x = applied(a, key, dflt)
    const y = applied(b, key, dflt)
    if ((key === 'SameIdInOutUniqueData' ? Boolean(x) !== Boolean(y) : x !== y) && !out.includes(words)) out.push(words)
  }
  return out
}
